import { ArrowLeft, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileLayout } from "../components/layout/MobileLayout.jsx";
import { Button } from "../components/ui/Button.jsx";
import { Card, CardContent } from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select.jsx";
import { Textarea } from "../components/ui/TextArea.jsx";
import { useFormValidation } from "../hooks/useFormValidation.js";
import { useToast } from "../hooks/useToast.js";
import { getCurrentUser } from "../services/authService.js";
import { fetchFields } from "../services/dynamicFieldService.js";
import { createLead } from "../services/leadService.js";
import { fetchUsersByRole } from "../services/teamService.js";
import { VALIDATION_RULES } from "../utils/validation";
import { buildFieldPayload } from "../utils/fieldMapping.js";
import VoiceLeadInput from "../components/VoiceLeadInput.jsx";

const addLeadValidation = {
  leadName: VALIDATION_RULES.full_name,
  phone: VALIDATION_RULES.phone_required,
  email: {
    required: { value: true, message: "Email is required" },
    pattern: {
      regex: /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: "Email must include '@' and '.' in a valid format",
    },
  },
  address: {
    required: false,
    pattern: {
      regex: /^[a-zA-Z0-9\s.,'-]+$/,
      message: "Address contains invalid characters",
    },
    minLength: { value: 10, message: "Address must be at least 10 characters" },
    maxLength: { value: 500, message: "Address must be under 500 characters" },
  },
  assignedTo: { required: false },
  notes: {
    required: false,
    maxLength: { value: 500, message: "Notes must be under 500 characters" },
  },
};

export default function AddLead() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const {
    form: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    setFieldValue,
  } = useFormValidation(
    {
      leadName: "",
      phone: "",
      email: "",
      address: "",
      assignedTo: "",
      notes: "",
    },
    addLeadValidation
  );

  const { leadName, phone, email, address, assignedTo, notes } = formData;

  const [reps, setReps] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Dynamic fields (custom dropdowns from Settings)
  const [dynamicFields, setDynamicFields] = useState([]);
  const [dynamicValues, setDynamicValues] = useState({});


  // Lead source (system field — extracted from dynamic fields for guaranteed visibility)
  const [leadSourceField, setLeadSourceField] = useState(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState("");

  // Other dynamic fields (non-system, optional)
  const [otherFields, setOtherFields] = useState([]);

  // Validation attempted flag
  const [attempted, setAttempted] = useState(false);

  // Voice-to-lead: auto-fill form from parsed voice data
  const handleVoiceParsed = (data) => {
    // Map form field names to AI output keys
    const formMapping = {
      leadName: ["name", "full_name", "lead_name"],
      phone: ["phone_number", "phone", "mobile"],
      email: ["email", "email_address"],
      address: ["address", "location", "full_address"],
      notes: ["notes", "comment", "remarks", "additional_info"],
    };

    // Auto-fill all form fields
    for (const [formKey, aiKeys] of Object.entries(formMapping)) {
      for (const aiKey of aiKeys) {
        if (data[aiKey]) {
          setFieldValue(formKey, data[aiKey]);
          break;
        }
      }
    }

    // Build address from parts if no direct address
    if (!data.address && (data.city || data.state)) {
      const parts = [data.city, data.state].filter(Boolean);
      if (parts.length) setFieldValue("address", parts.join(", "));
    }

    // Match lead source by option label (try multiple keys)
    const sourceValue = data.source || data.lead_source || data.reference;
    if (sourceValue && leadSourceField?.options?.length) {
      const match = leadSourceField.options.find(
        (o) => o.option_label.toLowerCase() === sourceValue.toLowerCase()
      );
      if (match) setSelectedLeadSource(match.id);
    }

    // Match ALL dynamic fields — check every key in AI response against every field
    if (otherFields.length > 0) {
      const newDynamic = { ...dynamicValues };

      otherFields.forEach((field) => {
        if (!field.options?.length) return;

        // Try matching by field label as key (e.g., "Service Name" → "service_name")
        const snakeKey = field.field_label.toLowerCase().replace(/\s+/g, "_");
        const possibleValues = [
          data[snakeKey],
          data[field.field_label.toLowerCase()],
          data[field.field_label],
        ].filter(Boolean);

        // Also search all AI response values for option matches
        for (const [, val] of Object.entries(data)) {
          if (typeof val === "string" && val.length > 1) {
            const optMatch = field.options.find(
              (o) => o.option_label.toLowerCase() === val.toLowerCase()
            );
            if (optMatch) {
              newDynamic[field.id] = optMatch.id;
              return; // found match, stop checking this field
            }
          }
        }

        // Direct key match
        for (const val of possibleValues) {
          if (val) {
            const match = field.options.find(
              (o) => o.option_label.toLowerCase() === val.toLowerCase()
            );
            if (match) {
              newDynamic[field.id] = match.id;
              break;
            }
          }
        }
      });

      setDynamicValues(newDynamic);
    }

    // Collect any unmatched data into notes
    const mappedKeys = new Set([
      "name", "full_name", "lead_name", "phone_number", "phone", "mobile",
      "email", "email_address", "address", "location", "full_address",
      "notes", "comment", "remarks", "source", "lead_source", "reference",
      "city", "state",
      ...otherFields.map((f) => f.field_label.toLowerCase().replace(/\s+/g, "_")),
    ]);

    const extraNotes = Object.entries(data)
      .filter(([k, v]) => !mappedKeys.has(k) && v)
      .map(([k, v]) => `${k}: ${v}`)
      .join(", ");

    if (extraNotes) {
      setFieldValue("notes", [data.notes, extraNotes].filter(Boolean).join("\n"));
    }
  };

  useEffect(() => {
    (async () => {
      const res = await getCurrentUser();
      if (res.success) setCurrentUser(res.user);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchFields();
        const allFields = res.data || [];
        setDynamicFields(allFields);

        // Separate Lead Source (system) from other fields
        const sysField = allFields.find((f) => f.is_system);
        setLeadSourceField(sysField || null);
        setOtherFields(allFields.filter((f) => !f.is_system));
      } catch (err) {
        console.error("Failed to load dynamic fields:", err);
        setDynamicFields([]);
      }
    })();
  }, []);


  useEffect(() => {
    (async () => {
      try {
        const res = await fetchUsersByRole("sales_person", {
          page: 1,
          limit: 100,
          search: "",
        });
        const users = res?.users || res?.data || [];
        setReps(Array.isArray(users) ? users : []);
      } catch {
        setReps([]);
      }
    })();
  }, []);

  const getAssignOptions = () => {
    if (!currentUser) return [];
    if (currentUser.user_role === "business_admin") {
      return [
        { id: currentUser.id, name: "Self" },
        ...reps.map((rep) => ({ id: rep.id, name: rep.name })),
      ];
    }
    if (currentUser.user_role === "sales_person") {
      return [{ id: currentUser.id, name: "Self" }];
    }
    return [];
  };

  const handleDynamicFieldChange = (fieldId, optionId) => {
    setDynamicValues((prev) => ({ ...prev, [fieldId]: optionId }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttempted(true);

    if (!validateAll()) {
      toast({ title: "Validation Error", description: "Please fix the errors.", variant: "destructive" });
      return;
    }

    // Validate Lead Source (required)
    if (leadSourceField && !selectedLeadSource) {
      toast({ title: "Lead Source Required", description: "Please select a Lead Source.", variant: "destructive" });
      return;
    }


    try {
      setSubmitting(true);

      // Build dynamic field payload
      const dynamicPayload = {};
      const unmappedSelections = [];

      // Lead Source
      if (leadSourceField && selectedLeadSource) {
        const selectedOpt = leadSourceField.options.find((o) => o.id === selectedLeadSource);
        const fp = buildFieldPayload(leadSourceField.field_label, selectedLeadSource, selectedOpt?.option_label);
        if (fp) Object.assign(dynamicPayload, fp);
      }

      // Other dynamic fields
      otherFields.forEach((field) => {
        const selectedOptionId = dynamicValues[field.id];
        if (selectedOptionId) {
          const selectedOpt = field.options?.find((o) => o.id === selectedOptionId);
          const fp = buildFieldPayload(field.field_label, selectedOptionId, selectedOpt?.option_label);
          if (fp) {
            Object.assign(dynamicPayload, fp);
          } else {
            // Field has no DB column — store in notes
            unmappedSelections.push(`${field.field_label}: ${selectedOpt?.option_label || selectedOptionId}`);
          }
        }
      });

      const payload = {
        name: leadName,
        phone_number: phone,
        email: email || null,
        reference: null,
        agent_assigned: assignedTo || null,
        ...dynamicPayload,
        rate: null,
        tax_percent: null,
        address: address || null,
        state: null,
        city: null,
        pin_code: null,
        deal_status: "new",
        contact_time: null,
        comment: [
          ...(notes ? [{ comment_text: notes.trim(), contact_channel: null }] : []),
          ...(unmappedSelections.length > 0
            ? [{ comment_text: unmappedSelections.join("\n"), contact_channel: null }]
            : []),
        ],
      };

      const res = await createLead(payload);
      if (res?.success) {
        toast({
          title: "Lead Added Successfully",
          description: `${leadName} has been added to the system.`,
        });
        navigate("/leads");
      } else {
        toast({
          title: "Failed to add lead",
          description: res?.error || "Try again.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Unable to add lead",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileLayout userRole="business_admin">
      <div className="p-4 space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold">Add New Lead</h1>
        </div>

        {/* Voice-to-Lead */}
        <VoiceLeadInput onParsed={handleVoiceParsed} disabled={submitting} />

        <form onSubmit={handleSubmit} className="space-y-4">
          <Card className="bg-transparent border-none shadow-none">
            <CardContent className="pb-5 space-y-3 transition-shadow bg-white border-2 shadow-sm text-start border-blue-100/50 rounded-xl">
              <h2 className="font-bold">Lead Information</h2>

              {/* Lead Name */}
              <div>
                <Label>Lead Name *</Label>
                <Input
                  id="leadName"
                  name="leadName"
                  placeholder="Enter lead name"
                  value={leadName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  className={`h-8 text-xs ${errors.leadName ? "border-red-500" : ""}`}
                />
                {touched.leadName && errors.leadName && (
                  <p className="mt-1 text-[11px] text-red-500">{errors.leadName}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <Label>Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="9876543210"
                  value={phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  maxLength={10}
                  className={`h-8 text-xs ${errors.phone ? "border-red-500" : ""}`}
                />
                {touched.phone && errors.phone && (
                  <p className="mt-1 text-[11px] text-red-500">{errors.phone}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label>Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="lead@example.com"
                  value={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`h-8 text-xs ${errors.email ? "border-red-500" : ""}`}
                />
                {touched.email && errors.email && (
                  <p className="mt-1 text-[11px] text-red-500">{errors.email}</p>
                )}
              </div>

              {/* ── Lead Source (REQUIRED - always visible) ── */}
              {leadSourceField && (
                <div>
                  <Label>Lead Source *</Label>
                  {leadSourceField.options?.length > 0 ? (
                    <Select
                      value={selectedLeadSource}
                      onValueChange={setSelectedLeadSource}
                    >
                      <SelectTrigger
                        className={`h-8 text-xs ${
                          attempted && !selectedLeadSource ? "border-red-500" : ""
                        }`}
                      >
                        <SelectValue placeholder="Select Lead Source" />
                      </SelectTrigger>
                      <SelectContent>
                        {leadSourceField.options.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            {opt.option_label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-[11px] text-red-400 mt-1">
                      No lead source options configured. Go to Settings to add options.
                    </p>
                  )}
                  {attempted && !selectedLeadSource && (
                    <p className="mt-1 text-[11px] text-red-500">Lead Source is required</p>
                  )}
                </div>
              )}

              {/* ── Other Dynamic Fields (optional) ── */}
              {otherFields.map((field) => (
                <div key={field.id}>
                  <Label>{field.field_label}</Label>
                  {field.options?.length > 0 ? (
                    <Select
                      value={dynamicValues[field.id] || ""}
                      onValueChange={(value) =>
                        handleDynamicFieldChange(field.id, value)
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={`Select ${field.field_label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            {opt.option_label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-[11px] text-gray-400 mt-1">
                      No options added yet.
                    </p>
                  )}
                </div>
              ))}

              {/* Address */}
              <div>
                <Label>Address</Label>
                <Textarea
                  id="address"
                  name="address"
                  placeholder="Enter complete address"
                  value={address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={2}
                  className="text-xs"
                />
                {touched.address && errors.address && (
                  <p className="mt-1 text-[11px] text-red-500">{errors.address}</p>
                )}
              </div>

              {/* Assign To */}
              <div>
                <Label>Assign To</Label>
                {currentUser && (
                  <Select
                    value={
                      currentUser.user_role === "business_admin"
                        ? assignedTo || ""
                        : assignedTo || String(currentUser.id)
                    }
                    onValueChange={(value) => setFieldValue("assignedTo", value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select Sales Person" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAssignOptions().map((user) => (
                        <SelectItem key={user.id} value={String(user.id)}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Notes */}
              <div>
                <Label>Initial Notes</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  placeholder="Add any initial notes about this lead..."
                  value={notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={2}
                  className="text-xs"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              className="h-8 px-3 text-xs rounded-full"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="h-8 px-3 text-xs text-white border rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
            >
              <Plus size={12} className="mr-1" />
              {submitting ? "Adding..." : "Add Lead"}
            </Button>
          </div>
        </form>
      </div>
    </MobileLayout>
  );
}
